import React, { useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import PageHeader from "../../../components/layout/PageHeader";
import {
  Button,
  EmptyState,
  failedEmptyState,
  useLoadPhase,
  useToast,
} from "../../../components/ui";
import { BILLING_SETTINGS_PATH, billingResultPath } from "../../../routes/paths";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { settingsCrumbs } from "../../settings/settingsTabs";
import BillingDetailsForm, { controlId } from "../components/BillingDetailsForm";
import CheckoutSkeleton from "../components/CheckoutSkeleton";
import OrderSummary from "../components/OrderSummary";
import PaymentMethods from "../components/PaymentMethods";
import { stashResult } from "../checkoutOutcome";
import { useRazorpayCheckout } from "../hooks/useRazorpayCheckout";
import { useBillingForm } from "../hooks/useBillingForm";
import { usePlans } from "../hooks/usePlans";
import { useQuote } from "../hooks/useQuote";
import {
  SUBSCRIPTIONS_LIVE,
  buySubscription,
  verifySubscription,
} from "../services/subscriptionsService";
import { toInternationalNumber } from "../../../lib/fieldFormat";

/**
 * Buying a plan: who it is billed to on the left, what it costs on the right,
 * and one button.
 *
 * The whole payment step belongs to the gateway. There is no method to pick
 * here, no card to type, no OTP to wait for — pressing Pay now creates the
 * subscription on our backend and hands it to Razorpay's own window, which
 * draws every one of those itself. See `useRazorpayCheckout`, which is also
 * where the reason lives: no card field on this origin is what keeps the PCI
 * obligation at its smallest.
 *
 * Which plan is in the URL — `/billing/yearly` — rather than in router state,
 * so the page survives a reload and can be linked to. That is a departure from
 * the rule the rest of `paths` follows, and a deliberate one: the param names a
 * product cycle, not a record, so there is nothing to enumerate and nothing
 * about a person in it. A cycle the price list has never heard of is sent back
 * to the plans rather than shown a broken checkout.
 *
 * The form opens on the billing profile the workspace has already saved, and on
 * `My brand` where it has none — a dietitian who has set their practice up has
 * answered most of this once, and should not have to again to buy. What is
 * typed here is written back to the profile on the way to the payment, so the
 * second purchase asks for nothing.
 *
 * Every figure on the right comes from `POST /billing/quote/`, and the total
 * under Pay now is the total the backend would charge. See `quoteService` for
 * why none of it is worked out here.
 *
 * There is no coupon row. Nothing on the wire carries a code — neither the
 * quote nor the purchase — so a box to type one into would be a control that
 * reaches nobody, and a discount line beside it would be answering a question
 * this checkout cannot ask. Where the server takes something off an order of
 * its own accord it comes back inside the quote, already off the subtotal the
 * summary shows.
 */
const BillingCheckoutPage: React.FC = () => {
  const { cycle } = useParams<{ cycle: string }>();
  const toast = useToast();
  const navigate = useNavigate();
  const getToken = useTokenGetter();
  const razorpay = useRazorpayCheckout();

  // What is on sale, off the backend's own price list — the same call the cards
  // this page was opened from drew themselves with, so what is charged here is
  // what was advertised there. Nothing on this screen is priced until it lands.
  const priceList = usePlans();
  const plan = priceList.plans.find((candidate) => candidate.id === cycle);

  // Who this workspace is invoiced, filled from the saved billing profile and
  // the brand behind it, with everything wrong with it as it is typed — see
  // `useBillingForm`, which the trial's detail step shares. Called before the
  // early return below can be reached — hooks cannot be skipped — which costs
  // two fetches on a URL naming a plan that does not exist, and buys a
  // component whose hook order never changes.
  const form = useBillingForm();

  // What the order comes to, off the backend. Asked as soon as there is a plan
  // code to ask about — see `useQuote`.
  const quote = useQuote(plan?.code, plan?.id);
  const price = quote.price;

  // The loads this page waits on, as one wait: the plan it is pricing, what
  // that comes to, and the details it fills itself with. A checkout that
  // appeared a piece at a time would read as a page that broke and then
  // repaired itself — see `CheckoutSkeleton`. Answers that arrive quickly open
  // the page straight away; the shape only stands in once the wait is long
  // enough to notice.
  const waiting =
    form.loading || priceList.status === "loading" || quote.status === "loading";
  const phase = useLoadPhase(waiting);

  const [paying, setPaying] = useState(false);

  // A cycle no plan answers to — a URL typed by hand, or a cycle that has
  // stopped being sold. Sent back to the plans rather than shown an empty
  // checkout: there is nothing on this screen without a plan, and the page that
  // has one is a click away. Only once the list is actually known, since until
  // then every cycle looks like one nothing answers to.
  if (priceList.status === "ready" && !plan) {
    return <Navigate to={BILLING_SETTINGS_PATH} replace />;
  }

  // No plan, or no price for it: still on their way, or they never came.
  // Nothing of the checkout is drawn either way — a form somebody can fill in
  // and press Pay on, above a price that could not be loaded, is the one
  // version of this screen that must not exist.
  if (!plan || !price) {
    const failed = priceList.status === "failed" || quote.status === "failed";

    return (
      <div className="px-4 py-5 sm:px-6 lg:px-8 3xl:px-12 3xl:py-8">
        {/* Without the plan the trail cannot name what is being bought, so it
            stops at the tab — the crumbs still lead back out, which is the
            whole of what they are for here. */}
        <PageHeader
          title="Complete your subscription"
          breadcrumbs={settingsCrumbs("billings")}
        />

        {failed ? (
          <EmptyState
            {...failedEmptyState(
              // One message for both, because they are one thing to the buyer:
              // the plan and what it costs are not two facts they asked for
              // separately. Which of the two calls failed is in the console.
              "We could not price this plan",
              <Button
                size="sm"
                variant="secondary"
                onClick={
                  priceList.status === "failed"
                    ? priceList.reload
                    : quote.reload
                }
              >
                Try again
              </Button>
            )}
          />
        ) : (
          <>
            <p role="status" className="sr-only">
              Loading this plan…
            </p>
            <div className="mt-4 3xl:mt-6 grid grid-cols-1 items-start gap-5 lg:grid-cols-[minmax(0,1fr)_400px] 3xl:gap-6 4xl:lg:grid-cols-[minmax(0,1fr)_460px]">
              <CheckoutSkeleton phase={phase} />
            </div>
          </>
        )}
      </div>
    );
  }

  /** Ends the checkout on the result route, which confirms it against the
   *  backend before believing any of it. */
  const settle = (
    outcome: Parameters<typeof stashResult>[1],
    extra: Parameters<typeof stashResult>[2] = { subscriptionId: null }
  ) => {
    stashResult(plan.id, outcome, extra);
    navigate(billingResultPath(plan.id));
  };

  /**
   * The payment window was closed without finishing.
   *
   * Deliberately not a trip to the result screen. Nothing was charged, nothing
   * was decided, and the commonest reason by far is a misclick or a change of
   * mind — so the checkout stays exactly as it was, filled in, one press away
   * from trying again. Sending somebody to a "payment cancelled" page and
   * making them navigate back is a punishment for closing a box.
   *
   * It is still said out loud rather than passed over in silence: a window
   * that vanishes with no word leaves people wondering whether it went
   * through.
   */
  const handleDismissed = () => {
    setPaying(false);
    toast.info(
      "Payment cancelled",
      "The payment window was closed and nothing has been charged. Your details are still here - press Pay now whenever you are ready."
    );
  };

  /**
   * Paying.
   *
   * Validated here rather than by disabling the button: a Pay that is greyed
   * out says nothing about which of eight fields is in the way, and this is
   * the last screen anyone wants to be guessing on. So it stays pressable,
   * every field is marked visited at once, and the first one that fails is
   * focused.
   *
   * Past that the order of events is fixed, and each step is somebody else's
   * job: the billing profile is recorded, our backend creates the subscription
   * and hands back its id and the *public* key; Razorpay's window takes the
   * payment; our backend checks the signature. The browser decides none of it —
   * what comes out of the window is an account of what happened, and the result
   * screen confirms it against the webhook the backend has actually seen.
   *
   * The details go up before anything else happens, and a refusal stops the
   * press. They are what the invoice will be headed with and what the tax is
   * worked out from — a payment taken against details the server never accepted
   * is a receipt nobody can raise.
   */
  const handlePay = async () => {
    const firstFailing = form.firstFailing;

    if (firstFailing) {
      // Everything at once: a form submitted whole should show everything
      // wrong with it, not the next problem one at a time.
      form.showAllErrors();
      document.getElementById(controlId(firstFailing))?.focus();
      toast.error(
        "Check your billing details",
        "Some of what an invoice needs is missing or does not look right."
      );
      return;
    }

    // Recorded before a rupee moves, and on every path below — including the
    // ones that cannot take a payment at all, because the details are the
    // buyer's own and having typed them once is enough either way.
    setPaying(true);
    try {
      const stored = await form.save(form.details);
      // What came back rather than what went up: the server trims and
      // normalises, and the form should end on what the invoice will carry.
      form.reset(stored);
    } catch (cause) {
      setPaying(false);
      toast.error(
        "Could not save your billing details",
        cause instanceof Error
          ? cause.message
          : "We could not reach the server. Please try again."
      );
      console.error("billing profile failed to save:", cause);
      return;
    }

    // Bought. One value goes up and it is not a figure - the plan by its code -
    // because what this costs is the server's to decide at the moment of
    // purchase, not the summary's to assert. What comes back is the
    // subscription, the price it was bought at, and the key and id the window
    // needs to take a mandate against it.
    try {
      const purchase = await buySubscription(getToken, plan.code);

      await razorpay.open({
        options: {
          // Nothing about how the window looks is sent from here. The name, the
          // mark, the colour and the row of methods are the Razorpay
          // dashboard's, read off the account this key belongs to — so what a
          // buyer sees is what was configured there, and a change made there
          // takes effect without this app shipping anything.
          key: purchase.checkout.key,
          subscription_id: purchase.checkout.subscriptionId,
          // No amount: a subscription is priced by the plan behind it, and a
          // figure sent from here would be a second opinion the gateway is
          // right to ignore.
          description:
            purchase.checkout.description ||
            `${plan.productName} - ${plan.cycleLabel}`,
          // Saves the buyer retyping what they have already given us. None of
          // it is card data; it is the same billing details on the left.
          prefill: {
            name: form.details.fullName,
            email: form.details.email,
            contact: toInternationalNumber(form.details.phone),
          },
        },
        onSuccess: async (response) => {
          // Handed straight on to the backend, which recomputes the signature
          // with the secret. Nothing here treats an unverified response as a
          // paid subscription - and nothing is lost while the endpoint that
          // checks it is still to be built, because the webhook activates the
          // subscription whether or not this browser ever reports in.
          if (SUBSCRIPTIONS_LIVE) {
            try {
              await verifySubscription(getToken, purchase.subscription.id, {
                razorpayPaymentId: response.razorpay_payment_id,
                // Always present on a subscription's response. Defaulted rather
                // than asserted so a malformed one fails the backend's check -
                // which is where a signature is supposed to fail - instead of
                // throwing here and skipping the check entirely.
                razorpaySignature: response.razorpay_signature ?? "",
              });
            } catch (cause) {
              console.error("subscription verify failed:", cause);
            }
          }

          settle(
            { state: "active" },
            {
              subscriptionId: purchase.subscription.id,
              paymentId: response.razorpay_payment_id,
              // What it was actually bought at, off the quote that came back
              // with the purchase. Carried across so the receipt quotes the
              // charge rather than re-deriving one from the price list - which
              // it cannot do, since it does not know what coupon was on it.
              amount: purchase.price.total,
            }
          );
        },
        onFailure: (reason) => {
          setPaying(false);
          settle(
            { state: "failed", reason },
            {
              subscriptionId: purchase.subscription.id,
              amount: purchase.price.total,
            }
          );
        },
        onDismiss: handleDismissed,
      });
    } catch (cause) {
      setPaying(false);
      const message =
        cause instanceof Error
          ? cause.message
          : "We could not start the payment. Please try again.";
      toast.error("Could not start the payment", message);
      console.error("subscription purchase failed:", cause);
    }
  };

  return (
    <div className="px-4 py-5 sm:px-6 lg:px-8 3xl:px-12 3xl:py-8">
      {/* The trail back to the plans, as every screen that sits under another
          one carries it. Billing is a Settings tab rather than a screen of its
          own, so the trail starts where the rail item does and names the tab
          the way the tab bar does — `settingsCrumbs` is where both come from.

          The step this page is names what is being bought rather than
          repeating the title — and names it the way the rest of the feature
          does, `Product - Cycle`, so the crumb, the status card and the cancel
          dialog all call one plan by one name. */}
      <PageHeader
        title="Complete your subscription"
        breadcrumbs={[
          ...settingsCrumbs("billings"),
          { label: `${plan.productName} - ${plan.cycleLabel}` },
        ]}
      />

      {/* Said as soon as the request goes out, whether or not anything is drawn
          for it: the blocks that go up behind it are the shape of an answer
          rather than the answer, and are hidden from screen readers. */}
      {waiting && (
        <p role="status" className="sr-only">
          Loading your saved details…
        </p>
      )}

      {/* The form takes what is left; the summary keeps a fixed column, since a
          list of figures does not get better for being wider.

          Both columns wait on the same load, even though only the left one
          depends on it: a checkout that appears a piece at a time — a finished
          summary, then a form under it a moment later — reads as a page that
          broke and then repaired itself. See `CheckoutSkeleton`. */}
      <div className="mt-4 3xl:mt-6 grid grid-cols-1 items-start gap-5 lg:grid-cols-[minmax(0,1fr)_400px] 3xl:gap-6 4xl:lg:grid-cols-[minmax(0,1fr)_460px]">
        {waiting ? (
          <CheckoutSkeleton phase={phase} />
        ) : (
          <>
            <div>
              {form.loadFailed && (
                // Not a failure worth blocking on: these are the buyer's own
                // details, and the form works perfectly well filled in by
                // hand. It says why it is empty so nobody thinks the app has
                // forgotten them.
                //
                // Either load failing gets the same line. To the buyer they are
                // one thing — "what you gave us before" — and which of the two
                // records did not arrive is not a distinction they can act on.
                <p className="mb-3 rounded-md border border-line bg-[#FAFBFC] px-4 py-3 text-[13px] 3xl:text-[14px] text-ink-muted">
                  We could not load your saved details, so the form below is
                  empty. Filling it in here works just the same.
                </p>
              )}
              <BillingDetailsForm
                details={form.details}
                onChange={form.change}
                errors={form.errors}
                touched={form.touched}
                onVisit={form.visit}
                busy={paying}
              />
            </div>

            {/* Dropped by the height of a form caption, so the summary card
                starts on the line the form's first card does. Every card on the
                left is a `FormSection`, which carries an `h-8` caption and an
                `mb-2` under it — 40px this column has nothing to fill with, its
                own heading being inside the card rather than above it. Only
                from `lg`, where the two are side by side at all; stacked, there
                is no line to share. The brand screen lines the same two up the
                other way round — see the note in `FormSection`. */}
            <div className="flex flex-col gap-4 lg:mt-10">
              <OrderSummary
                plan={plan}
                price={price}
                onPay={handlePay}
                paying={paying}
              />

              <PaymentMethods />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BillingCheckoutPage;
